import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import api, { apiErr, fileUrl } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { ClipboardCheck, MapPin, AlertTriangle, X, Bell, Calendar, PlusCircle } from "lucide-react";
import { PeoplePicker, PhotoPicker, TAG_STYLE, inp, lbl } from "@/components/MaintenanceFields";

const STATUS = { open: "Ανοιχτή", in_progress: "Σε εξέλιξη", done: "Ολοκληρωμένη" };
const EMPTY = { action: "", resolved: true, notify_manager: false, photo_file_ids: [], next_action: "", reminder_date: "", reminder_user_ids: [] };

const TaskCard = ({ t, tagName, onOpen }) => (
  <button onClick={onOpen} data-testid={`my-task-${t.id}`}
    className={`text-left rounded-2xl bg-white border p-4 hover:border-[#B9AE9A] ${t.overdue ? "border-[#FCA5A5] ring-1 ring-[#FCA5A5]" : "border-[#E3DED4]"}`}>
    <div className="text-[11px] uppercase tracking-wider text-[#8A8577] font-semibold inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {t.villa_name}{t.location ? ` · ${t.location}` : ""}</div>
    <div className="font-medium text-[#45423C] mt-0.5">{t.title}</div>
    <div className="flex flex-wrap gap-1 mt-1.5">
      {(t.tags || []).map((g) => <span key={g} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${TAG_STYLE[g]}`}>{tagName[g] || g}</span>)}
    </div>
    <div className="mt-2 text-xs text-[#6F6A60] flex flex-wrap gap-3">
      <span>{STATUS[t.status]}</span>
      {t.due_date && <span className={t.overdue ? "text-[#991B1B] font-semibold inline-flex items-center gap-1" : ""}>{t.overdue && <AlertTriangle className="h-3 w-3" />}έως {t.due_date}</span>}
      {t.actions?.length > 0 && <span>{t.actions.length} ενέργειες</span>}
    </div>
  </button>
);

const ActionDialog = ({ t, people, onClose }) => {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const save = useMutation({
    mutationFn: async () => (await api.post(`/maintenance/tasks/${t.id}/actions`, f)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["my-maint"] }); toast.success(f.resolved ? "Ολοκληρώθηκε" : "Η ενέργεια καταχωρήθηκε"); onClose(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="action-dialog">
      <div className="bg-white rounded-2xl w-full max-w-xl my-6 p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-xs text-[#8A8577]">{t.villa_name}{t.location ? ` · ${t.location}` : ""}</div>
            <h2 className="font-serif text-2xl text-[#45423C]">{t.title}</h2>
            {t.description && <p className="text-sm text-[#6F6A60] mt-1">{t.description}</p>}
          </div>
          <button onClick={onClose} data-testid="action-close" className="p-1.5 rounded-lg hover:bg-[#F1EDE6]"><X className="h-5 w-5" /></button>
        </div>
        {t.photo_file_ids?.length > 0 && <div className="flex gap-2 flex-wrap">{t.photo_file_ids.map((p) => <img key={p} src={fileUrl(p)} alt="" className="h-16 w-16 rounded-lg object-cover" />)}</div>}
        {t.actions?.length > 0 && (
          <div className="rounded-xl bg-[#F7F5F1] p-3 space-y-2 text-sm" data-testid="action-history">
            {t.actions.map((a) => (
              <div key={a.id}><span className="text-[11px] text-[#8A8577]">{a.at.slice(0, 16).replace("T", " ")} · {a.by_name}</span><div className="text-[#45423C]">{a.action}{a.next_action ? ` → ${a.next_action}` : ""}</div></div>
            ))}
          </div>
        )}
        <div>
          <label className={lbl}>Τι έκανες *</label>
          <textarea data-testid="action-text" rows={3} value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} className={inp} />
        </div>
        <div className="flex gap-2">
          {[[true, "Επιλύθηκε"], [false, "Δεν επιλύθηκε"]].map(([v, l]) => (
            <button key={l} type="button" data-testid={`action-resolved-${v}`} onClick={() => setF({ ...f, resolved: v })}
              className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold border ${f.resolved === v ? (v ? "bg-[#6E7A63] text-white border-[#6E7A63]" : "bg-[#C86235] text-white border-[#C86235]") : "bg-white border-[#E3DED4] text-[#45423C]"}`}>{l}</button>
          ))}
        </div>
        <div>
          <label className={lbl}>Φωτογραφία</label>
          <PhotoPicker testid="action-photos" value={f.photo_file_ids} onChange={(v) => setF({ ...f, photo_file_ids: v })} />
        </div>
        {!f.resolved && (
          <>
            <label className="flex items-center gap-2 text-sm text-[#45423C]">
              <input data-testid="action-notify" type="checkbox" checked={f.notify_manager} onChange={(e) => setF({ ...f, notify_manager: e.target.checked })} />
              <Bell className="h-4 w-4" /> Ειδοποίηση διευθυντή
            </label>
            <div>
              <label className={lbl}>Πρόταση επόμενης ενέργειας</label>
              <input data-testid="action-next" value={f.next_action} onChange={(e) => setF({ ...f, next_action: e.target.value })} className={inp} placeholder="π.χ. Χρειάζεται υδραυλικός, ανταλλακτικό" />
            </div>
          </>
        )}
        <div className="rounded-xl border border-[#E3DED4] p-3 space-y-2">
          <label className={`${lbl} inline-flex items-center gap-1`}><Calendar className="h-3 w-3" /> Υπενθύμιση (προαιρετικό)</label>
          <input data-testid="action-reminder-date" type="date" value={f.reminder_date} onChange={(e) => setF({ ...f, reminder_date: e.target.value })} className={`${inp} sm:!w-48`} />
          {f.reminder_date && (
            <>
              <div className="text-xs text-[#6F6A60]">Σε ποιους; (αν δεν επιλέξεις, μόνο σε εσένα)</div>
              <PeoplePicker testid="action-reminder-people" people={people} value={f.reminder_user_ids} onChange={(v) => setF({ ...f, reminder_user_ids: v })} />
            </>
          )}
        </div>
        <button data-testid="action-save" disabled={!f.action.trim() || save.isPending} onClick={() => save.mutate()}
          className="w-full rounded-xl bg-[#45423C] text-white px-6 py-3 text-sm font-semibold disabled:opacity-50">{save.isPending ? "Αποθήκευση…" : "Καταχώρηση ενέργειας"}</button>
      </div>
    </div>
  );
};

export default function MyMaintenance() {
  const [params] = useSearchParams();
  const [scope, setScope] = useState(params.get("scope") === "all" ? "all" : "mine");
  const [openId, setOpenId] = useState(null);
  const { data: meta } = useQuery({ queryKey: ["maint-meta"], queryFn: async () => (await api.get("/maintenance/meta")).data });
  const { data: people = [] } = useQuery({ queryKey: ["maint-people"], queryFn: async () => (await api.get("/maintenance/people")).data });
  const { data: tasks = [] } = useQuery({ queryKey: ["my-maint", scope], queryFn: async () => (await api.get(`/maintenance/my-tasks?scope=${scope}`)).data });
  const tagName = Object.fromEntries((meta?.tags || []).map((g) => [g.id, g.name]));
  const open = tasks.find((t) => t.id === openId);
  const overdue = tasks.filter((t) => t.overdue).length;
  return (
    <div className="space-y-5" data-testid="my-maintenance-page">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Συντήρηση</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2"><ClipboardCheck className="h-7 w-7" /> Οι Εργασίες μου</h1>
        </div>
        <Link to="/maintenance/new" data-testid="maint-new-link" className="ml-auto inline-flex items-center gap-1.5 rounded-xl bg-[#C86235] text-white px-4 py-2 text-sm font-semibold hover:bg-[#a24a24]"><PlusCircle className="h-4 w-4" /> Νέα βλάβη</Link>
        <div className="inline-flex rounded-xl border border-[#E3DED4] overflow-hidden">
          {[["mine", "Δικές μου"], ["all", "Όλες"]].map(([k, l]) => (
            <button key={k} data-testid={`scope-${k}`} onClick={() => setScope(k)} className={`px-4 py-2 text-sm font-medium ${scope === k ? "bg-[#45423C] text-white" : "bg-white text-[#45423C]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-2 text-xs">
        <span className="px-3 py-1.5 rounded-full bg-[#FEF3C7] text-[#92400E] font-semibold" data-testid="my-count">Ενεργές: {tasks.length}</span>
        {overdue > 0 && <span className="px-3 py-1.5 rounded-full bg-[#FEE2E2] text-[#991B1B] font-semibold" data-testid="my-overdue">Ληξιπρόθεσμες: {overdue}</span>}
      </div>
      {tasks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E3DED4] p-10 text-center text-[#6F6A60]" data-testid="my-empty">
          {scope === "mine" ? "Δεν έχεις ενεργές εργασίες." : "Δεν υπάρχουν ενεργές εργασίες."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {tasks.map((t) => <TaskCard key={t.id} t={t} tagName={tagName} onOpen={() => setOpenId(t.id)} />)}
        </div>
      )}
      {open && <ActionDialog t={open} people={people} onClose={() => setOpenId(null)} />}
    </div>
  );
}
